/**
 * Dynamic Swiper Loader
 * Only loads Swiper library when a swiper section enters the viewport
 * Reduces initial JavaScript payload by ~80KB
 */

(function() {
  'use strict';

  // Configuration
  const config = {
    rootMargin: '200px 0px', // Load Swiper 200px before section enters viewport
    threshold: 0.01,
    swiperCSSUrl: null, // Will be set from data attribute
    swiperJSUrl: null,  // Will be set from data attribute
    loadTimeout: 5000    // Fallback timeout for loading
  };

  // State management
  let swiperLoading = false;
  let swiperLoaded = false;
  let pendingSwipers = [];
  const initializedSwipers = new WeakMap();

  /**
   * Load Swiper CSS
   */
  function loadSwiperCSS() {
    return new Promise((resolve, reject) => {
      // Check if CSS is already loaded
      const existingLink = document.querySelector('link[href*="smi-swiper-bundle"]');
      if (existingLink) {
        resolve();
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = config.swiperCSSUrl;
      link.onload = resolve;
      link.onerror = reject;

      document.head.appendChild(link);
    });
  }

  /**
   * Load Swiper JS
   */
  function loadSwiperJS() {
    return new Promise((resolve, reject) => {
      // Check if Swiper is already loaded
      if (window.Swiper) {
        resolve(window.Swiper);
        return;
      }

      // Check if script is already loading
      const existingScript = document.querySelector('script[src*="smi-swiper-bundle"]');
      if (existingScript) {
        // Wait for it to load
        existingScript.addEventListener('load', () => resolve(window.Swiper));
        existingScript.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      script.src = config.swiperJSUrl;
      script.async = true;
      script.onload = () => resolve(window.Swiper);
      script.onerror = reject;

      document.head.appendChild(script);
    });
  }

  /**
   * Load Swiper library (CSS + JS)
   */
  function loadSwiperLibrary() {
    if (swiperLoaded) {
      return Promise.resolve(window.Swiper);
    }

    if (swiperLoading) {
      // Return a promise that waits for the current load
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (swiperLoaded) {
            clearInterval(checkInterval);
            resolve(window.Swiper);
          }
        }, 50);

        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!swiperLoaded) {
            reject(new Error('Swiper load timeout'));
          }
        }, config.loadTimeout);
      });
    }

    swiperLoading = true;

    return Promise.all([
      loadSwiperCSS(),
      loadSwiperJS()
    ])
      .then(([, Swiper]) => {
        swiperLoaded = true;
        swiperLoading = false;
        return Swiper;
      })
      .catch(err => {
        swiperLoading = false;
        console.error('Failed to load Swiper:', err);
        throw err;
      });
  }

  /**
   * Parse Swiper options from data attribute
   */
  function parseSwiperOptions(element) {
    const optionsAttr = element.dataset.swiperOptions || element.getAttribute('data-swiper-options');

    if (!optionsAttr) {
      return {};
    }

    try {
      // Parse JSON options
      const options = JSON.parse(optionsAttr);

      // Handle pagination
      if (element.querySelector('.swiper-pagination')) {
        options.pagination = {
          el: element.querySelector('.swiper-pagination'),
          clickable: true,
          dynamicBullets: true
        };
      }

      // Handle navigation
      const prevButton = element.parentElement.querySelector('.arrow-slider-left, .swiper-button-prev');
      const nextButton = element.parentElement.querySelector('.arrow-slider-right, .swiper-button-next');

      if (prevButton && nextButton) {
        options.navigation = {
          prevEl: prevButton,
          nextEl: nextButton
        };
      }

      // Optimize for performance
      options.observer = true;
      options.observeParents = true;
      options.watchSlidesProgress = true;
      options.lazy = {
        loadPrevNext: true,
        loadPrevNextAmount: 2
      };

      return options;
    } catch (err) {
      console.warn('Failed to parse Swiper options:', err);
      return {};
    }
  }

  /**
   * Initialize a single Swiper instance
   */
  function initSwiper(element) {
    // Check if already initialized
    if (initializedSwipers.has(element)) {
      return initializedSwipers.get(element);
    }

    // Mark as loading
    element.classList.add('swiper-loading');

    return loadSwiperLibrary()
      .then(Swiper => {
        const options = parseSwiperOptions(element);

        // Initialize Swiper
        const swiperInstance = new Swiper(element, options);

        // Mark as initialized
        element.classList.remove('swiper-loading');
        element.classList.add('swiper-initialized');
        initializedSwipers.set(element, swiperInstance);

        return swiperInstance;
      })
      .catch(err => {
        console.error('Failed to initialize Swiper:', err);
        element.classList.remove('swiper-loading');
        element.classList.add('swiper-error');
      });
  }

  /**
   * IntersectionObserver callback
   */
  function handleIntersection(entries, observer) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const swiperElement = entry.target;

        // Unobserve immediately
        observer.unobserve(swiperElement);

        // Initialize Swiper
        initSwiper(swiperElement);
      }
    });
  }

  /**
   * Initialize the dynamic loader
   */
  function init() {
    // Get Swiper URLs from the first swiper element or inline script
    const firstSwiper = document.querySelector('.swiper[data-swiper-options], .smi-swiper');

    if (!firstSwiper) {
      return; // No swipers on this page
    }

    // Try to extract URLs from existing script tags (in case they're preloaded)
    const swiperScript = document.querySelector('script[src*="smi-swiper-bundle.min.js"]');
    const swiperCSS = document.querySelector('link[href*="smi-swiper-bundle.min.css"]');

    if (swiperScript) {
      config.swiperJSUrl = swiperScript.src;
      // Remove the preloaded script to save bandwidth
      swiperScript.remove();
    }

    if (swiperCSS) {
      config.swiperCSSUrl = swiperCSS.href;
      // Remove the preloaded CSS to save bandwidth (will load on demand)
      swiperCSS.remove();
    }

    // Fallback: construct URLs if not found
    if (!config.swiperJSUrl) {
      const assetUrl = firstSwiper.dataset.assetUrl || '';
      config.swiperJSUrl = assetUrl.replace(/\/[^/]*$/, '/smi-swiper-bundle.min.js');
      config.swiperCSSUrl = assetUrl.replace(/\/[^/]*$/, '/smi-swiper-bundle.min.css');
    }

    // Add loading styles
    if (!document.getElementById('swiper-loader-styles')) {
      const style = document.createElement('style');
      style.id = 'swiper-loader-styles';
      style.textContent = `
        .swiper-loading {
          opacity: 0.7;
          pointer-events: none;
        }
        .swiper-initialized {
          opacity: 1;
          transition: opacity 0.3s ease;
        }
        .swiper-error {
          opacity: 0.5;
        }
      `;
      document.head.appendChild(style);
    }

    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: Load Swiper immediately and initialize all instances
      const allSwipers = document.querySAll('.swiper[data-swiper-options], .smi-swiper');

      loadSwiperLibrary().then(() => {
        allSwipers.forEach(swiper => initSwiper(swiper));
      });

      return;
    }

    // Create IntersectionObserver
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: config.rootMargin,
      threshold: config.threshold
    });

    // Observe all Swiper elements
    const allSwipers = document.querySelectorAll('.swiper[data-swiper-options], .smi-swiper');

    allSwipers.forEach(swiper => {
      observer.observe(swiper);
    });
  }

  /**
   * Expose public API
   */
  window.SwiperDynamicLoader = {
    init: init,
    loadSwiper: loadSwiperLibrary,
    initSwiper: initSwiper,
    config: config
  };

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Use requestIdleCallback if available for non-blocking init
    if ('requestIdleCallback' in window) {
      requestIdleCallback(init, { timeout: 500 });
    } else {
      setTimeout(init, 0);
    }
  }

})();
